import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import ApexCharts from 'apexcharts';
import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTooltip,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { finalize } from 'rxjs';
import { TabelaPaginacaoComponent } from 'src/app/shared/components/tabela-paginacao/tabela-paginacao.component';
import { Column } from '../../interface/colunas-tabelas.interface';
import {
  PaginationMetricas,
  TabelasObrasFase,
} from '../../interface/tabela-obras-fase.interface';
import { DropdownService } from '../../service/dropdown-metricas.service';
import { MetricasService } from '../../service/metricas.service';
import { FiltroPeriodoComponent } from '../filtros-metricas/filtro-periodo/filtro-periodo.component';
import { PeriodoService } from '../filtros-metricas/filtro-periodo/service/filtro-periodo.service';
import { FiltroResponsavelComponent } from '../filtros-metricas/filtro-responsavel/filtro-responsavel.component';
import { FiltroTipoObraComponent } from '../filtros-metricas/filtro-tipo-obra/filtro-tipo-obra.component';
import { OBRAS_POR_FASE_COLUMNS } from './obras-por-fase.columns';
import { environment } from 'src/environments/environment';
import { ModalAguardandoDownloadComponent } from 'src/app/shared/components/modal-aguardando-download/modal-aguardando-download.component';

(window as any).ApexCharts = ApexCharts;

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  responsive: ApexResponsive[];
  colors: string[];
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
  legend?: any;
};

export interface MarketPhase {
  marketPhaseID: number;
  marketPhase: string;
  workQuantity: number;
}

@Component({
  selector: 'app-obras-por-fase',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    FiltroPeriodoComponent,
    FiltroTipoObraComponent,
    FiltroResponsavelComponent,
    TabelaPaginacaoComponent,
    ModalAguardandoDownloadComponent,
  ],
  templateUrl: './obras-por-fase.component.html',
  styleUrls: ['./obras-por-fase.component.scss'],
})
export class ObrasPorFaseComponent implements OnInit {
  private subscriptions: any[] = [];
  columns: Column[] = OBRAS_POR_FASE_COLUMNS;
  processandoAcao: boolean = false;
  mensagemProcessando: string = 'Aguardando download...';

  trackByFn = (index: number, item: any) => item.id;

  BASE_IMG_URL: string = environment.BASE_IMG_URL;

  fasesDaObra: MarketPhase[] = [];
  todasAsFases: MarketPhase[] = [];

  selectedFaseObra: number | 'Todas' = 'Todas';

  config: Partial<BsDaterangepickerConfig> = {
    containerClass: 'bs-datepicker',
    rangeInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    selectFromOtherMonth: true,
    isAnimated: true,
    displayMonths: 1,
  };

  responsaveis: [] = [];

  currentPage: number = 1;
  pageNumber: number = 1;
  pageSize: number = 50;

  idObraSelecionada: number | null = null;
  indiceObraAtual: number = 0;
  exibirFichaDaObra: boolean = false;

  viewMode: 'chart' | 'table' = 'chart';

  tiposSelecionados: any;

  filtroFaseObraSelecionados: number[] = [];

  get desabilitarExportar(): boolean {
    return this.fasesDaObra.length === 0 && this.tableData.length === 0;
  }

  mostrarFaseObra = false;

  @Input() selectedPeriodo: Date[] | null = null;
  @Input() selectedUsuario: string = 'Todos';
  @Input() obraIds: number[] = [];
  @Input() responsavel: any[] = [];

  @Output() rowClick = new EventEmitter<any>();
  @Output() obraSelecionada = new EventEmitter<{
    workId: number;
    obraIds: number[];
    moduleId?: number;
    workType?: string;
    workTypesPorObra?: Record<number, number>;
  }>();
  @Output() avancar = new EventEmitter<void>();
  @Output() voltarObra = new EventEmitter<void>();
  @Output() companyNameClick = new EventEmitter<{
    companyId?: number;
    companyName?: string;
    workType?: string;
  }>();
  @Output() atualizandoMetricasChange = new EventEmitter<boolean>();
  @Output() periodoChange = new EventEmitter<{
    periodBegin: string;
    periodEnd: string;
  } | null>();

  @ViewChild('chart') chart?: ChartComponent;

  chartReady = false;

  chartOptions!: ChartOptions;
  intervaloSelecionado: Date[] | null = null;
  tableData: TabelasObrasFase[] = [];
  pagination: PaginationMetricas = {
    pageNext: 2,
    pagePrev: 0,
    recordsCount: 0,
    pageFirst: 1,
    pageLast: 1,
    pageActive: true,
    page: 1,
    pageSize: 10,
    shortcuts: 0,
    hasValue: false,
    shortcutPages: [],
    records: [],
  };
  private periodoService = inject(PeriodoService);

  periodoSelecionado: Date[] | null = null;

  constructor(
    private _metricasService: MetricasService,
    private dropdownService: DropdownService
  ) {}

  ngOnInit(): void {
    this.filterWorkPhases();
    const dropdownSub = this.dropdownService
      .getActiveDropdown()
      .subscribe((active) => {
        if (active !== 'faseObra') {
          this.mostrarFaseObra = false;
        }
      });
    this.subscriptions.push(dropdownSub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['responsavel'] && !changes['responsavel'].firstChange) {
      const responsaveis = changes['responsavel'].currentValue;

      if (
        Array.isArray(responsaveis) &&
        responsaveis.length > 0 &&
        responsaveis[0]?.userLevelId
      ) {
        const p = this.periodObjFromSelected();
        this.listMarketPhase(p);
        this.listMarketPhaseTable(p);
      }
    }

    if (changes['selectedPeriodo'] && !changes['selectedPeriodo'].firstChange) {
      if (this.responsavel?.length > 0 && this.responsavel[0]?.userLevelId) {
        const p = this.periodObjFromSelected();
        this.listMarketPhase(p);
        this.listMarketPhaseTable(p);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onResponsaveisRecebidos(resps) {
    if (!Array.isArray(resps)) {
      console.error('Responsáveis recebidos não são um array:', resps);
      return;
    }
    if (resps.length === 0) {
      console.warn('Array de responsáveis está vazio.');
    }
    if (resps.length > 0 && resps[0].id === undefined) {
      console.error('Responsável não possui propriedade id:', resps[0]);
    }
  }

  get totalPages(): number {
    return this.totalPagesValue;
  }

  totalPagesValue: number = 0;

  onPageChange(page: number): void {
    this.pagination.page = page;
    this.listMarketPhaseTable();
  }

  toggleFaseObra() {
    const isOpening = !this.mostrarFaseObra;
    this.dropdownService.setActiveDropdown(isOpening ? 'faseObra' : null);
    this.mostrarFaseObra = isOpening;
  }

  listMarketPhaseTable(periodo?: {
    periodBegin: string;
    periodEnd: string;
  }): void {
    this.atualizandoMetricasChange.emit(true);

    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      return;
    }

    const marketPhaseID = this.filtroFaseObraSelecionados[0] || 0;

    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }

    const requestBody = {
      marketPhaseID: marketPhaseID,
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
      pagination: {
        page: this.currentPage,
        pageSize: this.pageSize,
        shortcuts: 10,
        ordenation: '',
      },
    };
    const sub = this._metricasService
      .listWorkPhaseTable(requestBody)
      .pipe(
        finalize(() => {
          this.atualizandoMetricasChange.emit(false);
        })
      )
      .subscribe(
        (response) => {
          if (response && response.data && response.data.records) {
            this.tableData = response.data.records.map((item: any) => ({
              responsibleId: item.responsibleId,
              responsibleName: item.responsibleName,
              stateId: item.stateId,
              stateName: ` ${item.cityName} - ${item.stateName} `,
              cityId: item.cityId,
              cityName: item.cityName,
              workId: item.workId,
              companyId: item.companyId,
              companyName: item.companyName,
              workName: item.workName,
              workTypeId: item.workTypeId,
              workType: item.workType,
              marketPhaseID: item.marketPhaseID,
              marketPhase: item.marketPhase,
              lastAction: item.lastAction,
              lastActionDate: formatDate(
                item.lastActionDate,
                'dd/MM/yyyy',
                'en-US'
              ),
              lastNote: item.lastNote,
              lastPotential: item.lastPotential,
              viewStatus: item.viewStatus,
              assignedDate: formatDate(
                item.assignedDate,
                'dd/MM/yyyy',
                'en-US'
              ),
            }));

            this.obraIds = this.tableData.map((obra) => obra.workId);

            const totalRegistros =
              response.data.totalRecords ?? response.data.recordsCount ?? 0;

            this.totalPagesValue = Math.ceil(totalRegistros / this.pageSize);

            this.pagination = {
              ...this.pagination,
              page: this.currentPage,
              recordsCount: totalRegistros,
              pageFirst: 1,
              pageLast: this.totalPagesValue,
              pageNext:
                this.currentPage < this.totalPagesValue
                  ? this.currentPage + 1
                  : null,
              pagePrev: this.currentPage > 1 ? this.currentPage - 1 : null,
              hasValue: this.tableData.length > 0,
              records: this.tableData,
            };
          } else {
            console.error('response.data.records não está definido:', response);
            this.tableData = [];
          }
        },
        (error) => {
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
    this.subscriptions.push(sub);
  }

  mudarPagina(novaPagina: number): void {
    this.currentPage = novaPagina;
    this.listMarketPhaseTable();
  }

  listMarketPhase(periodo?: { periodBegin: string; periodEnd: string }): void {
    this.atualizandoMetricasChange.emit(true);
    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      return;
    }

    const marketPhaseID = this.filtroFaseObraSelecionados[0] || 0;
    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }

    const requestBody = {
      marketPhaseID: marketPhaseID,
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
    };
    this.atualizandoMetricasChange.emit(true);
    const sub = this._metricasService
      .listWorkByMarketPhase(requestBody)
      .pipe(finalize(() => this.atualizandoMetricasChange.emit(false)))
      .subscribe(
        (response) => {
          this.atualizandoMetricasChange.emit(false);
          if (response && response.data && response.data.length > 0) {
            this.fasesDaObra = response.data;

            const series = response.data.map(
              (item: MarketPhase) => item.workQuantity
            );
            const labels = response.data.map(
              (item: MarketPhase) => item.marketPhase
            );

            this.chartOptions = {
              series: series,
              chart: {
                type: 'pie',
                height: 260,
              },
              labels: labels,
              colors: ['#252422', '#b4ada1', '#f2efe5', '#eb5e28', '#6b6864'],
              plotOptions: {
                pie: {
                  donut: {
                    size: '40%',
                  },
                },
              },
              tooltip: {
                y: {
                  formatter: (val: number) => `${val} obras`,
                },
              },
              legend: {
                show: false,
              },
              responsive: [
                {
                  breakpoint: 768,
                  options: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                },
              ],
            };

            this.chartReady = true;
          } else {
            this.fasesDaObra = [];
            this.chartReady = false;
          }
        },
        (error) => {
          this.atualizandoMetricasChange.emit(false);
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
    this.subscriptions.push(sub);
  }
  private parseYmdToLocal(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  onPeriodoSelecionadoChange(
    periodo: { periodBegin: string; periodEnd: string } | null
  ) {
    if (periodo && periodo.periodBegin && periodo.periodEnd) {
      this.selectedPeriodo = [
        this.parseYmdToLocal(periodo.periodBegin),
        this.parseYmdToLocal(periodo.periodEnd),
      ];
    } else {
      this.selectedPeriodo = null;
    }
    this.listMarketPhase(periodo ?? undefined);
    this.listMarketPhaseTable(periodo ?? undefined);
  }

  private formatYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDateToBackendFormat(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  private periodObjFromSelected():
    | { periodBegin: string; periodEnd: string }
    | undefined {
    if (this.selectedPeriodo?.length === 2) {
      return {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    return undefined;
  }

  toggleView(mode: 'chart' | 'table') {
    this.viewMode = mode;
  }

  exportExcel() {
    this.mensagemProcessando = 'Aguardando download do Excel...';
    this.processandoAcao = true;
    const filtros = this.capturarFiltrosAtuais();
    this._metricasService.downloadExcelWorkByMarketPhase(filtros).subscribe(
      (response) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'obras-por-fases.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.processandoAcao = false;
      },
      (error) => {
        this.processandoAcao = false;
        console.error('Erro ao exportar Excel:', error);
      }
    );
  }

  onTiposSelecionadosChange(tipos: number[]): void {
    this.tiposSelecionados = tipos;
    let periodo: { periodBegin: string; periodEnd: string } | undefined =
      undefined;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      periodo = {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    this.listMarketPhase(periodo);
    this.listMarketPhaseTable(periodo);
  }

  capturarFiltrosAtuais(): any {
    const filtros: any = {};

    let period: { periodBegin: string; periodEnd: string } | null = null;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      period = {
        periodBegin: this.formatDateToBackendFormat(this.selectedPeriodo[0]),
        periodEnd: this.formatDateToBackendFormat(this.selectedPeriodo[1]),
      };
    }

    if (period) {
      filtros.period = period;
    }

    let responsibleId: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleId = Number(this.selectedUsuario) || 0;
    }

    if (responsibleId !== 0) {
      filtros.responsible = {
        responsible: responsibleId
      };
    }

    const faseId = this.filtroFaseObraSelecionados[0] || 0;
    if (faseId !== 0) {
      filtros.phase = {
        phase: faseId
      };
    }

    const tiposObraIds = this.tiposSelecionados || [];
    if (tiposObraIds.length > 0) {
      filtros.type = {
        ids: tiposObraIds
      };
    }

    return filtros;
  }

  limparFasesObra(): void {
    this.filtroFaseObraSelecionados = [];
    this.mostrarFaseObra = false;
    const periodo = this.periodObjFromSelected();
    this.listMarketPhase(periodo);
    this.listMarketPhaseTable(periodo);
  }

  selecionarFase(fase: string) {
    this.filtroFaseObraSelecionados.push(Number(fase));
    const periodo = this.periodObjFromSelected();
    this.listMarketPhase(periodo);
    this.listMarketPhaseTable(periodo);
  }

  onFaseChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;
    this.atualizandoMetricasChange.emit(true);

    if (valor === 'Todas') {
      this.filtroFaseObraSelecionados = [];
    } else {
      const faseSelecionada = Number(valor);
      this.filtroFaseObraSelecionados = [faseSelecionada];
    }

    const periodo =
      this.selectedPeriodo?.length === 2
        ? {
            periodBegin: this.formatYmd(this.selectedPeriodo[0]),
            periodEnd: this.formatYmd(this.selectedPeriodo[1]),
          }
        : { periodBegin: '', periodEnd: '' };

    this.listMarketPhase(periodo);
    this.listMarketPhaseTable(periodo);

    this.mostrarFaseObra = false;
    this.atualizandoMetricasChange.emit(false);
  }

  onResponsavelIdChange(responsavelId: number): void {
    const periodo = this.periodObjFromSelected();
    this.listMarketPhase(periodo);
    this.listMarketPhaseTable(periodo);
  }

  onWorkNameSelected(workId: number): void {
    const obra = this.tableData.find((data) => data.workId === workId);
    const workTypesPorObra: Record<number, number> = {};
    this.tableData.forEach((data) => {
      if (data.workTypeId != null) workTypesPorObra[data.workId] = data.workTypeId;
    });
    this.obraSelecionada.emit({
      workId,
      obraIds: this.tableData.map((data) => data.workId),
      ...(obra?.workTypeId != null ? { moduleId: obra.workTypeId } : {}),
      ...(obra?.workType ? { workType: obra.workType } : {}),
      workTypesPorObra,
    });
  }

  onCompanyNameClick(row: any): void {
    this.companyNameClick.emit({
      companyId: row.companyId,
      companyName: row.companyName,
      workType: row.workType,
    });
  }

  filterWorkPhases(): void {
    const sub = this._metricasService.listFilterWorkPhase().subscribe(
      (response) => {
        this.todasAsFases = response.data;
      },
      (error) => {
        console.error('Erro ao buscar fases de obra:', error);
      }
    );
    this.subscriptions.push(sub);
  }
}
